import { useEffect, useRef, useState } from 'react';
import { mutateConfig } from '../api/mutationConfig';
import { getErrorMessage } from '../lib/apiError';

const MAX_BYTES = 2_048_000;

export type UploadMode = 'pass' | 'hero';

export interface UploadedImage {
  uri: string;
  derived: string;
}

interface UploadResponse {
  uri: string;
  uri_1x?: string;
  preview_uri?: string;
}

const uploadUrl: Record<UploadMode, string> = {
  pass: '/v1/uploads?generate_1x=true',
  hero: '/v1/uploads?generate_preview=true',
};

const derivedLabel: Record<UploadMode, string> = { pass: '1x copy', hero: 'Preview' };

const formatSize = (bytes: number) => `${(bytes / 1_000_000).toFixed(1)} MB`;

interface ImageUploadFieldProps {
  id: string;
  label: string;
  hint: string;
  mode: UploadMode;
  value: UploadedImage | null;
  onChange: (value: UploadedImage | null) => void;
  onBusyChange: (busy: boolean) => void;
  error?: string;
  removable?: boolean;
  disabled?: boolean;
}

export const ImageUploadField = ({
  id,
  label,
  hint,
  mode,
  value,
  onChange,
  onBusyChange,
  error,
  removable = false,
  disabled = false,
}: ImageUploadFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState('');
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  const { mutate: upload, isPending } = mutateConfig({
    onUploadProgress: ({ progress: p }) => setProgress(typeof p === 'number' ? p : null),
    // A key no query uses, so an upload doesn't refetch everything.
  })<UploadResponse>({ url: uploadUrl[mode], cacheKeys: ['uploads'] });

  useEffect(
    () => () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    },
    [localPreview]
  );

  const handleFile = (file: File) => {
    setUploadError('');
    if (!file.type.startsWith('image/')) {
      setUploadError(`"${file.name}" isn't an image. Choose a PNG or JPEG file.`);
      return;
    }
    if (file.size >= MAX_BYTES) {
      setUploadError(`"${file.name}" is ${formatSize(file.size)}. Images must be under 2 MB.`);
      return;
    }

    const body = new FormData();
    body.append('file', file);
    setLocalPreview(URL.createObjectURL(file));
    setProgress(0);
    onBusyChange(true);

    upload(body, {
      onSuccess: ({ payload }) => {
        const derived = mode === 'pass' ? payload?.uri_1x : payload?.preview_uri;
        if (!payload?.uri || !derived) {
          setUploadError('The upload finished, but the server did not return every image address. Try again.');
          return;
        }
        onChange({ uri: payload.uri, derived });
      },
      onError: (err) => setUploadError(getErrorMessage(err, `Couldn't upload "${file.name}". Try again.`)),
      onSettled: () => {
        setProgress(null);
        setLocalPreview(null);
        onBusyChange(false);
      },
    });
  };

  const shownImage = localPreview ?? value?.uri ?? null;
  const message = uploadError || error;
  const percent = progress === null ? 0 : Math.round(progress * 100);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <label htmlFor={id} className="text-sm font-medium text-gray-700">
          {label}
        </label>
        <p className="text-xs text-gray-500">{hint}</p>
      </div>
      <div className="flex items-start gap-4">
        <div
          className={`flex h-24 w-40 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-gray-50 ${
            message ? 'border-red-300' : 'border-gray-200'
          }`}
        >
          {shownImage ? (
            <img
              src={shownImage}
              alt={`${label} preview`}
              className={`max-h-full max-w-full object-contain ${isPending ? 'opacity-50' : ''}`}
            />
          ) : (
            <span className="text-xs text-gray-400">No image</span>
          )}
        </div>
        <div className="flex min-w-0 flex-col gap-2">
          <input
            ref={inputRef}
            id={id}
            type="file"
            accept="image/*"
            className="sr-only"
            disabled={disabled || isPending}
            aria-invalid={message ? true : undefined}
            aria-describedby={message ? `${id}-error` : undefined}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) handleFile(file);
            }}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={disabled || isPending}
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {value ? 'Replace' : 'Choose image'}
            </button>
            {removable && value && !isPending && (
              <button
                type="button"
                onClick={() => onChange(null)}
                disabled={disabled}
                className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors disabled:opacity-40"
              >
                Remove
              </button>
            )}
          </div>
          {isPending && (
            <div className="flex flex-col gap-1" aria-live="polite">
              <div className="h-1.5 w-40 overflow-hidden rounded-full bg-gray-200">
                <div className="h-full bg-gray-900 transition-[width]" style={{ width: `${percent}%` }} />
              </div>
              <span className="text-xs text-gray-500">
                {progress === null || progress < 1 ? `Uploading… ${percent}%` : 'Processing…'}
              </span>
            </div>
          )}
          {!isPending && value && (
            <span className="text-xs text-gray-500">
              Saved.{' '}
              <a href={value.uri} target="_blank" rel="noreferrer" className="underline hover:text-gray-900">
                {mode === 'pass' ? '2x' : 'Original'}
              </a>
              {value.derived && (
                <>
                  {' · '}
                  <a href={value.derived} target="_blank" rel="noreferrer" className="underline hover:text-gray-900">
                    {derivedLabel[mode]}
                  </a>
                </>
              )}
            </span>
          )}
          {message && (
            <p id={`${id}-error`} className="text-xs text-red-600">
              {message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
